/**
 * Canonical capability constants for Carmel Work.
 *
 * Capabilities are the atomic units of authorization. Application behavior
 * must consume capabilities, not role names directly.
 *
 * Naming convention: <domain>.<action>.<scope>
 *   domain:   dashboard, task, approval, analytics, report, organisation, user, role, audit, intervention
 *   action:   view, create, assign, update, reassign, cancel, manage, request
 *   scope:    own, department, all, organisation, cross_department
 */

// ── Dashboard Capabilities ─────────────────────────────────────────

export const CAPABILITIES = {
  // Dashboard
  DASHBOARD_VIEW_PERSONAL: "dashboard.view.personal",
  DASHBOARD_VIEW_DEPARTMENT: "dashboard.view.department",
  DASHBOARD_VIEW_ORGANISATION: "dashboard.view.organisation",

  // Task
  TASK_VIEW_OWN: "task.view.own",
  TASK_VIEW_DEPARTMENT: "task.view.department",
  TASK_VIEW_ALL: "task.view.all",
  TASK_CREATE: "task.create",
  TASK_ASSIGN_SELF: "task.assign.self",
  TASK_ASSIGN_DEPARTMENT: "task.assign.department",
  TASK_ASSIGN_CROSS_DEPARTMENT: "task.assign.cross_department",
  TASK_ASSIGN_ALL: "task.assign.all",
  TASK_UPDATE_EXECUTION: "task.update.execution",
  TASK_UPDATE_DEPARTMENT: "task.update.department",
  TASK_UPDATE_ALL: "task.update.all",
  TASK_REASSIGN_DEPARTMENT: "task.reassign.department",
  TASK_REASSIGN_ALL: "task.reassign.all",
  TASK_CANCEL_DEPARTMENT: "task.cancel.department",
  TASK_CANCEL_ALL: "task.cancel.all",

  // Approval
  APPROVAL_REQUEST: "approval.request",
  APPROVAL_MANAGE_DEPARTMENT: "approval.manage.department",
  APPROVAL_MANAGE_ALL: "approval.manage.all",

  // Analytics
  ANALYTICS_VIEW_DEPARTMENT: "analytics.view.department",
  ANALYTICS_VIEW_ORGANISATION: "analytics.view.organisation",

  // Reports
  REPORT_VIEW_DEPARTMENT: "report.view.department",
  REPORT_VIEW_ALL: "report.view.all",

  // Organisation
  ORGANISATION_MANAGE: "organisation.manage",
  USER_MANAGE: "user.manage",
  ROLE_MANAGE: "role.manage",
  AUDIT_VIEW: "audit.view",

  // Intervention (Oversight module)
  INTERVENTION_MANAGE_ORGANISATION: "intervention.manage.organisation",
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

/** All capability values as an array. */
export const ALL_CAPABILITIES: readonly Capability[] = Object.values(CAPABILITIES);
